'use client';

import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import { z } from 'zod';
import { Building2 } from 'lucide-react';
import { useLocale } from 'next-intl';

import { formatPropertyAddress } from '@addava/skills/presentation';
import { Trans } from '~/components/ui/trans';

import { SaveCompletionContext } from '~/components/onboarding/save-completion-context';
import { SavePropertyOnboarding } from '~/components/onboarding/save-property-onboarding';

import { ParcelSheet } from './parcel-sheet';

type Selection = {
  block: string;
  lot: string;
  address: string;
  isSignedIn: boolean;
  email?: string;
  /** Reopened from storage after a reload, rather than by a click. */
  restored?: boolean;
};
const intentKey = 'addava:pending-property-save';
const intentSchema = z.object({
  block: z.string().regex(/^[0-9A-Z]{1,6}$/i),
  lot: z.string().regex(/^[0-9A-Z]{1,6}$/i),
  address: z.string().min(1).max(400),
  expiresAt: z.number(),
});
const SaveContext = createContext<((selection: Selection) => void) | null>(null);

export function usePublicPropertySave() {
  return useContext(SaveContext);
}

/** Keep the interactive save flow outside the replaceable streamed records. */
export function PublicPropertySaveProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<Selection | null>(null);
  const locale = useLocale();
  // Persist only the selected public parcel across reloads while OTP/MFA is in progress.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(intentKey);
      if (!raw) return;
      const intent = intentSchema.parse(JSON.parse(raw));
      if (intent.expiresAt > Date.now())
        setSelection({ ...intent, isSignedIn: false, restored: true });
      else sessionStorage.removeItem(intentKey);
    } catch {
      try {
        sessionStorage.removeItem(intentKey);
      } catch {
        /* Browser storage may be disabled. */
      }
    }
  }, []);
  const select = (value: Selection) => {
    try {
      sessionStorage.setItem(
        intentKey,
        JSON.stringify({
          block: value.block,
          lot: value.lot,
          address: value.address,
          expiresAt: Date.now() + 30 * 60 * 1000,
        }),
      );
    } catch {
      /* Saving still works when browser storage is unavailable. */
    }
    setSelection(value);
  };
  const close = () => {
    try {
      sessionStorage.removeItem(intentKey);
    } catch {
      /* No storage to clear. */
    }
    setSelection(null);
  };
  return (
    <SaveContext value={select}>
      {children}
      <ParcelSheet
        open={Boolean(selection)}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title={<Trans i18nKey="parcelWatch.property.saveSheetTitle" />}
        description={
          <Trans
            i18nKey={
              selection?.isSignedIn
                ? 'parcelWatch.property.signedInSaveDescription'
                : 'parcelWatch.property.saveSheetDescription'
            }
          />
        }
      >
        {selection && (
          <>
            <div className="pw-sheet-property">
              <Building2 className="size-5" />
              <strong>{formatPropertyAddress(selection.address)}</strong>
            </div>
            <SaveCompletionContext value={close}>
              <SavePropertyOnboarding
                address={selection.address}
                block={selection.block}
                lot={selection.lot}
                locale={locale}
                defaultEmail={selection.email}
                autoSave={!selection.restored}
              />
            </SaveCompletionContext>
          </>
        )}
      </ParcelSheet>
    </SaveContext>
  );
}
